<template>
    <VJoyPanel v-if="projectInformation.freelancerAppraisal?.rating">
        <template #panel-title>
            <div class="flex">
                {{ $t('projectclient.projectview.details.freelancer-review.title', {0: projectInformation.freelancer.firstName}) }}
                <NuxtLink
                    v-if="!wasAppraisalCreatedMoreThanOneMonthAgo && isAllowedToLeaveAnAppraisal"
                    :to="projectAppraisalLink"
                    external
                    class="review-edition-button"
                >
                    <VJoyIconButton icon="pen" size="xxsmall" variant="ghost" />
                </NuxtLink>
            </div>
        </template>
        <template #panel-body>
            <div class="project-details-freelancer-review">
                <div>{{ $t('projectclient.projectview.details.freelancer-review.general.label') }}</div>
                <VJoyRatingStars class="rating-stars" :rating-value="projectInformation.freelancerAppraisal.rating.general" size="large" />
                <ShowMoreTextContent :lines="2">
                    <template #text-content>
                        <div style="margin-bottom: var(--joy-core-spacing-4)">{{ clientComment }}</div>
                    </template>
                    <template #additional-content>
                        <div v-if="hasAnyOtherReview" class="project-details-freelancer-review_categories / flex">
                            <div class="project-details-freelancer-review_categories_block">
                                <div class="project-details-freelancer-review_categories_block_title">
                                    {{ $t('projectclient.projectview.details.freelancer-review.communication.label') }}
                                </div>
                                <VJoyRatingStars class="rating-stars" :rating-value="projectInformation.freelancerAppraisal?.rating.communication" />
                            </div>
                            <div class="project-details-freelancer-review_categories_block">
                                <div class="project-details-freelancer-review_categories_block_title">
                                    {{ $t('projectclient.projectview.details.freelancer-review.delay.label') }}
                                </div>
                                <VJoyRatingStars class="rating-stars" :rating-value="projectInformation.freelancerAppraisal?.rating.delay" />
                            </div>
                            <div class="project-details-freelancer-review_categories_block">
                                <div class="project-details-freelancer-review_categories_block_title">
                                    {{ $t('projectclient.projectview.details.freelancer-review.quality.label') }}
                                </div>
                                <VJoyRatingStars class="rating-stars" :rating-value="projectInformation.freelancerAppraisal?.rating.quality" />
                            </div>
                        </div>
                    </template>
                </ShowMoreTextContent>
            </div>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useRoute} from '@typed-router';
    import {isBefore, subMonths} from 'date-fns';
    import {storeToRefs} from 'pinia';
    import {VJoyIconButton, VJoyPanel, VJoyRatingStars} from '@maltjoy/core-vue';
    import {useProjectInformationStore, useUserStore} from '@/common/stores';
    import {ShowMoreTextContent} from '@/common/ui';

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);
    const appraisalCreationDate = computed(() => projectInformation.value.freelancerAppraisal?.creationDate);

    const {
        params: {projectId},
    } = useRoute('project-projectId-index-details');

    const hasAnyOtherReview = computed(() => {
        const rating = projectInformation.value.freelancerAppraisal?.rating;
        if (rating) {
            return rating.communication || rating.delay || rating.quality;
        }
        return false;
    });

    const clientComment = computed(() => {
        return `"${projectInformation.value.freelancerAppraisal?.comment ?? '-'}"`;
    });

    const userStore = useUserStore();
    const {roles} = storeToRefs(userStore);

    const isParticipant = computed(() => projectInformation.value?.participant);
    const hasAuthorityToLeaveAnAppraisal = computed(() => roles.value.includes('ROLE_ADMIN_WRITE_APPRAISAL'));

    const isAllowedToLeaveAnAppraisal = computed(() => isParticipant.value || (!isParticipant.value && hasAuthorityToLeaveAnAppraisal.value));

    const wasAppraisalCreatedMoreThanOneMonthAgo = computed(
        () =>
            appraisalCreationDate.value &&
            isBefore(appraisalCreationDate.value, subMonths(projectInformation.value.currentDatetime ?? new Date(), 1)),
    );

    const projectAppraisalLink = computed(() => `/appraisal/freelancer-by-client/${projectId}/?origin=project-view`);
</script>

<style lang="scss" scoped>
    .project-details-freelancer-review {
        .rating-stars {
            margin: var(--joy-core-spacing-2) 0;

            :deep(.joy-icon) {
                color: var(--joy-color-secondary-30) !important;
            }
        }

        &_categories {
            flex-flow: row wrap;
            align-items: center;
            gap: var(--joy-core-spacing-6);
            margin: var(--joy-core-spacing-2) 0;

            &_block {
                &_title {
                    font-size: var(--joy-font-size-primary-200);
                }
            }
        }
    }

    :deep(.joy-panel__header-title *) {
        text-align: center;

        > a {
            text-align: right;
        }
    }

    .review-edition-button {
        :deep(.joy-button *) {
            color: var(--joy-color-secondary-30);
        }
    }
</style>
